<template>
  <div class="modal fade" tabindex="-1" id="whatsappConfigModal" data-bs-keyboard="false" data-bs-backdrop="static">
    <div class="modal-dialog modal-lg modal-dialog-centered">
      <div class="modal-content">
        <div class="modal-header bg-primary text-white">
          <h5 class="modal-title text-white">
            <i class="bi bi-whatsapp me-2"></i>
            Configuração WhatsApp Cloud API
          </h5>
          <button type="button" class="btn-close btn-close-white" @click="closeModal"></button>
        </div>
        <div class="modal-body">
          <form @submit.prevent="saveConfig">
            <div class="row">
              <div class="col-md-6 mb-3">
                <label for="phone_number_id" class="form-label">
                  Phone Number ID <span class="text-danger">*</span>
                </label>
                <input
                  type="text"
                  class="form-control"
                  id="phone_number_id"
                  v-model="form.phone_number_id"
                  required
                  placeholder="Ex: 123456789012345"
                />
                <small class="form-text text-muted">ID do número de telefone do WhatsApp Business</small>
              </div>

              <div class="col-md-6 mb-3">
                <label for="access_token" class="form-label">
                  Access Token <span class="text-danger">*</span>
                </label>
                <div class="input-group">
                  <input
                    :type="showPassword ? 'text' : 'password'"
                    class="form-control"
                    id="access_token"
                    v-model="form.access_token"
                    required
                    placeholder="Token de acesso"
                  />
                  <button
                    type="button"
                    class="btn btn-outline-secondary"
                    @click="togglePasswordVisibility"
                  >
                    <i class="bi" :class="showPassword ? 'bi-eye-slash' : 'bi-eye'"></i>
                  </button>
                </div>
                <small class="form-text text-muted">Token de acesso da API do WhatsApp</small>
              </div>

              <div class="col-md-6 mb-3">
                <label for="api_version" class="form-label">API Version</label>
                <input
                  type="text"
                  class="form-control"
                  id="api_version"
                  v-model="form.api_version"
                  placeholder="v21.0"
                />
                <small class="form-text text-muted">Versão da API (padrão: v21.0)</small>
              </div>

              <div class="col-md-6 mb-3">
                <label for="verify_token" class="form-label">Verify Token</label>
                <input
                  type="text"
                  class="form-control"
                  id="verify_token"
                  v-model="form.verify_token"
                  placeholder="Token para verificação do webhook"
                />
                <small class="form-text text-muted">Token usado para verificar o webhook</small>
              </div>
            </div>

            <hr class="my-4" />
            <h6 class="mb-3"><i class="bi bi-robot me-2"></i>Atendimento pelo assistente (IA)</h6>
            <div class="row">
              <div class="col-12 mb-3">
                <div class="form-check form-switch">
                  <input
                    class="form-check-input"
                    type="checkbox"
                    id="ai_attends_first"
                    v-model="form.ai_attends_first"
                  />
                  <label class="form-check-label" for="ai_attends_first">
                    Assistente atende primeiro
                  </label>
                </div>
                <small class="form-text text-muted">Novos chats começam com a IA. Desmarque para que um humano atenda primeiro.</small>
              </div>
              <div class="col-md-6 mb-3">
                <label for="inactivity_return_to_ai_hours" class="form-label">Devolver chat para a IA após (horas sem interação)</label>
                <input
                  type="number"
                  class="form-control"
                  id="inactivity_return_to_ai_hours"
                  v-model.number="form.inactivity_return_to_ai_hours"
                  min="0"
                  max="8760"
                  placeholder="24"
                />
                <small class="form-text text-muted">0 = desativado. Ex.: 24 = após 24h sem mensagens o chat volta para a IA.</small>
              </div>
              <div class="col-12 mb-3">
                <label for="assistant_prompt" class="form-label">Prompt personalizado do assistente</label>
                <textarea
                  class="form-control font-monospace"
                  id="assistant_prompt"
                  v-model="form.assistant_prompt"
                  rows="8"
                  placeholder="Deixe em branco para usar o prompt padrão. Use :context_block: no texto para inserir os dados da sessão (obrigatório para o assistente funcionar)."
                />
                <small class="form-text text-muted">Opcional. Instruções customizadas para a IA. Inclua <code>:context_block:</code> onde devem ser inseridos os dados da clínica/paciente.</small>
              </div>
            </div>

            <div class="alert alert-info">
              <i class="bi bi-info-circle me-2"></i>
              <strong>Como obter essas informações:</strong>
              <ul class="mb-0 mt-2">
                <li>Acesse o <a href="https://developers.facebook.com/apps" target="_blank">Facebook Developers</a></li>
                <li>Crie ou selecione seu app do WhatsApp Business</li>
                <li>Vá em "WhatsApp" > "API Setup"</li>
                <li>Copie o Phone Number ID e o Access Token</li>
              </ul>
            </div>

            <div class="d-flex gap-2 justify-content-between flex-wrap">
              <button
                v-if="connectionData?.can_disconnect && connectionStatus === 'connected'"
                type="button"
                class="btn btn-outline-danger"
                :disabled="loading"
                @click="disconnectWhatsapp"
              >
                <i class="bi bi-link-45deg"></i>
                Desconectar WhatsApp
              </button>
              <div class="d-flex gap-2 ms-auto">
                <button type="button" class="btn btn-secondary" @click="closeModal">
                  Cancelar
                </button>
                <button type="submit" class="btn btn-primary" :disabled="loading">
                  <span v-if="loading" class="spinner-border spinner-border-sm me-2"></span>
                  <i v-else class="bi bi-save me-2"></i>
                  {{ loading ? 'Salvando...' : 'Salvar Configuração' }}
                </button>
              </div>
            </div>
          </form>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import http from "@/helpers/http";
import Alert from "@/helpers/alert";
import Modal from "@/helpers/modal";

export default {
  name: 'WhatsappConfigModal',
  emits: ['config-saved'],
  data() {
    return {
      loading: false,
      connectionStatus: 'disconnected',
      connectionData: null,
      showPassword: false,
      form: {
        phone_number_id: '',
        access_token: '',
        api_version: 'v21.0',
        verify_token: '',
        ai_attends_first: true,
        inactivity_return_to_ai_hours: 24,
        assistant_prompt: '',
      },
      originalAccessToken: null,
    }
  },
  computed: {
    place() {
      return this.$store.state.auth.place;
    },
  },
  mounted() {
    Modal.onShow('#whatsappConfigModal', () => {
      this.showPassword = false;
      this.loadConfig();
      this.checkStatus();
    });
  },
  methods: {
    togglePasswordVisibility() {
      if (!this.showPassword) {
        if (this.form.access_token === '••••••••' && this.originalAccessToken) {
          this.form.access_token = this.originalAccessToken;
        }
      } else {
        if (this.form.access_token === this.originalAccessToken) {
          this.form.access_token = '••••••••';
        }
      }
      this.showPassword = !this.showPassword;
    },
    closeModal() {
      Modal.toggle('#whatsappConfigModal');
    },
    async loadConfig() {
      if (!this.place?.id_place) return;

      const response = await http.get('/ai/whatsapp/informations', {
        params: { id_place: this.place.id_place }
      });

      if (response.data) {
        this.form.phone_number_id = response.data.phone_number_id || '';
        this.form.access_token = response.data.access_token ? '••••••••' : '';
        this.form.api_version = response.data.api_version || 'v21.0';
        this.form.verify_token = response.data.verify_token || '';
        this.form.ai_attends_first = response.data.ai_attends_first !== false;
        this.form.inactivity_return_to_ai_hours = response.data.inactivity_return_to_ai_hours ?? 24;
        this.form.assistant_prompt = response.data.assistant_prompt ?? '';

        if (response.data.access_token) {
          this.originalAccessToken = response.data.access_token;
        }
      }
    },

    async checkStatus() {
      if (!this.place?.id_place) return;

      const response = await http.get('/ai/whatsapp/status', {
        params: { id_place: this.place.id_place }
      });

      this.connectionStatus = response.data.connected ? 'connected' : 'disconnected';
      this.connectionData = response.data;
    },

    async disconnectWhatsapp() {
      if (!this.place?.id_place) return;

      const result = await Swal.fire({
        title: 'Desconectar WhatsApp?',
        html: 'Todas as configurações da conexão serão removidas. Será necessário conectar novamente para usar o WhatsApp.',
        icon: 'warning',
        showCancelButton: true,
        confirmButtonColor: '#dc3545',
        cancelButtonColor: '#6c757d',
        confirmButtonText: 'Sim, desconectar',
        cancelButtonText: 'Cancelar',
      });

      if (!result.isConfirmed) return;

      this.loading = true;
      try {
        await http.post('/ai/whatsapp/disconnect', { id_place: this.place.id_place });
        Alert.toastSuccess('WhatsApp desconectado.');
        await this.checkStatus();
        this.loadConfig();
        this.$emit('config-saved');
        this.closeModal();
      } catch (error) {
        const msg = error.response?.data?.message || error.response?.status === 403
          ? 'Você não tem permissão para desconectar o WhatsApp.'
          : 'Erro ao desconectar.';
        Alert.toastError(msg);
      } finally {
        this.loading = false;
      }
    },

    async saveConfig() {
      if (!this.place?.id_place) {
        Alert.toastError('Selecione uma clínica primeiro');
        return;
      }

      this.loading = true;

      const payload = {
        id_place: this.place.id_place,
        phone_number_id: this.form.phone_number_id || null,
        access_token: this.form.access_token === '••••••••' ? (this.originalAccessToken || null) : this.form.access_token,
        api_version: this.form.api_version || 'v21.0',
        verify_token: this.form.verify_token,
        ai_attends_first: this.form.ai_attends_first,
        inactivity_return_to_ai_hours: this.form.inactivity_return_to_ai_hours ?? 24,
        assistant_prompt: this.form.assistant_prompt || null,
      };

      if (payload.phone_number_id && !payload.access_token) {
        Alert.toastError('Access Token é obrigatório quando informar Phone Number ID');
        this.loading = false;
        return;
      }

      await http.post('/ai/whatsapp/update', payload);

      Alert.toastSuccess('Configuração salva com sucesso!');
      await this.checkStatus();
      this.loading = false;
      this.$emit('config-saved');
      this.closeModal();
    },
  }
}
</script>

<style scoped>
.bg-primary {
  background-color: #780f5a !important;
}

.btn-primary {
  background-color: #780f5a;
  border-color: #780f5a;
}

.btn-primary:hover {
  background-color: #5d0c47;
  border-color: #5d0c47;
}

.alert-info a {
  color: #780f5a;
  font-weight: 600;
}

.alert-info ul {
  padding-left: 20px;
}
</style>

